// src/pages/orders/orderEditPanels.jsx
// Inline edit forms and the fulfilment action panel for the order detail page.
//
// Kept out of newOrder-details.js on purpose: that file already carries three data
// sources and ~520 lines of layout, and these panels are the only part with
// mutation state (dirty fields, in-flight saves, per-action errors).
import React, { useState } from "react";
import {
  setShippingAddress, setBillingAddress, setCustomer, setNote,
  cancelOrder, markRefunded, markPartiallyRefunded, fulfilAndTrack, clearTracking,
} from "../../actions/order_edit_actions";

// A card header with a working Edit toggle. Replaces the <a href="#/">Edit</a>
// placeholders, which have been inert since the page was written.
export function EditToggle({ editing, onClick, disabled, disabledReason }) {
  if (disabled) {
    return (
      <span className="ms-auto text-inverse text-opacity-25 small" title={disabledReason} style={{ cursor: "not-allowed" }}>
        Locked
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick}
      className="ms-auto btn btn-link p-0 text-decoration-none text-inverse text-opacity-50"
      style={{ fontSize: "inherit" }}>
      {editing ? "Cancel" : "Edit"}
    </button>
  );
}

function Feedback({ result }) {
  if (!result) return null;
  return (
    <div className={`small mt-2 ${result.ok ? "text-success" : "text-danger"}`}>
      {result.ok ? "Saved." : result.message}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div className="mb-2">
      <label className="form-label small text-inverse text-opacity-50 mb-1">{label}</label>
      <input className="form-control form-control-sm" type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// The page renders addresses as {line1,line2,city,state,zip,country}; the engine
// takes Saleor's vocabulary. Mapped explicitly — the two genuinely differ, and a
// wrong guess here writes the state into the postcode.
const toInput = (a) => ({
  streetAddress1: a.line1 || "",
  streetAddress2: a.line2 || "",
  city: a.city || "",
  countryArea: a.state || "",
  postalCode: a.zip || "",
});

export function AddressEditor({ gid, which, current, onSaved, onCancel }) {
  const [form, setForm] = useState({
    line1: current?.line1 || "", line2: current?.line2 || "", city: current?.city || "",
    state: current?.state || "", zip: current?.zip || "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setResult(null);
    const fn = which === "shipping" ? setShippingAddress : setBillingAddress;
    // country is intentionally not editable here: it drives tax and shipping
    // selection, and changing it on a placed order would silently invalidate both.
    const r = await fn(gid, toInput(form));
    setSaving(false); setResult(r);
    if (r.ok) onSaved();
  };

  return (
    <div>
      <Field label="Street" value={form.line1} onChange={set("line1")} />
      <Field label="Street 2" value={form.line2} onChange={set("line2")} />
      <Field label="City" value={form.city} onChange={set("city")} />
      <Field label="State / region" value={form.state} onChange={set("state")} />
      <Field label="Postcode" value={form.zip} onChange={set("zip")} />
      <div className="small text-inverse text-opacity-50 mb-2">
        Country is fixed — it drives tax and shipping on a placed order.
      </div>
      <button className="btn btn-sm btn-theme me-2" disabled={saving} onClick={save}>
        {saving ? "Saving…" : "Save address"}
      </button>
      <button className="btn btn-sm btn-outline-theme" disabled={saving} onClick={onCancel}>Cancel</button>
      <Feedback result={result} />
    </div>
  );
}

export function CustomerEditor({ gid, name, email, onSaved, onCancel }) {
  const parts = String(name || "").trim().split(/\s+/);
  const [first, setFirst] = useState(parts[0] || "");
  const [last, setLast] = useState(parts.slice(1).join(" "));
  const [mail, setMail] = useState(email || "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    setSaving(true); setResult(null);
    const r = await setCustomer(gid, { email: mail, firstName: first, lastName: last });
    setSaving(false); setResult(r);
    if (r.ok) onSaved();
  };

  return (
    <div>
      <Field label="First name" value={first} onChange={setFirst} />
      <Field label="Last name" value={last} onChange={setLast} />
      <Field label="Email" value={mail} onChange={setMail} type="email" />
      <div className="small text-inverse text-opacity-50 mb-2">
        Changes this order only — it never renames a customer account.
      </div>
      <button className="btn btn-sm btn-theme me-2" disabled={saving} onClick={save}>
        {saving ? "Saving…" : "Save customer"}
      </button>
      <button className="btn btn-sm btn-outline-theme" disabled={saving} onClick={onCancel}>Cancel</button>
      <Feedback result={result} />
    </div>
  );
}

export function NoteEditor({ gid, note, onSaved, onCancel }) {
  const [text, setText] = useState(note || "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    setSaving(true); setResult(null);
    const r = await setNote(gid, text);
    setSaving(false); setResult(r);
    if (r.ok) onSaved();
  };

  return (
    <div>
      <textarea className="form-control form-control-sm" rows="4" value={text}
        placeholder="e.g. customer called — leave with concierge"
        onChange={(e) => setText(e.target.value)} />
      <div className="mt-2">
        <button className="btn btn-sm btn-theme me-2" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save note"}
        </button>
        <button className="btn btn-sm btn-outline-theme" disabled={saving} onClick={onCancel}>Cancel</button>
      </div>
      <Feedback result={result} />
    </div>
  );
}

// Fulfilment actions.
//
// One dropdown rather than a row of buttons, because these are mutually exclusive
// outcomes for an order and several are irreversible. Each one that leaves the
// system asks for confirmation naming what it does to the customer.
const ACTIONS = [
  { key: "fulfil", label: "Mark shipped (fulfil + tracking)", needsTracking: true,
    confirm: "Mark shipped? This emails the customer and pushes the tracking number to PayPal, which releases the payment hold." },
  { key: "update_tracking", label: "Update / correct tracking number", needsTracking: true,
    confirm: "Replace the existing tracking number? The new number will be sent to PayPal and the customer." },
  { key: "remove_tracking", label: "Remove tracking (to re-enter)",
    confirm: "Clear the tracking number? Do this only to correct a mistake — you will need to re-add the correct number immediately after." },
  { key: "cancel", label: "Cancel order",
    confirm: "Cancel this order? Allocated stock is released." },
  { key: "refund", label: "Mark fully refunded",
    confirm: "Mark this order fully refunded? This records an instruction — the refunded total moves when the PSP webhook confirms it." },
  { key: "partial", label: "Mark partially refunded", needsAmount: true,
    confirm: "Record a partial refund instruction for this order?" },
];

export function FulfillmentActions({ gid, status, tracking, carrier, onDone }) {
  const [action, setAction] = useState("");
  const [trackingNo, setTrackingNo] = useState(tracking || "");
  const [carrierName, setCarrierName] = useState(carrier || "");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const chosen = ACTIONS.find((a) => a.key === action);

  const apply = async () => {
    if (!chosen) return;
    if (chosen.needsTracking && !trackingNo.trim()) {
      setResult({ ok: false, message: "A tracking number is required — without it the customer gets no email and the PayPal hold stays on." });
      return;
    }
    if (chosen.needsAmount && !(parseFloat(amount) > 0)) {
      setResult({ ok: false, message: "Enter the refund amount." });
      return;
    }
    if (!window.confirm(chosen.confirm)) return;
    setBusy(true); setResult(null);
    let r;
    if (chosen.key === "fulfil") r = await fulfilAndTrack(gid, trackingNo.trim(), carrierName.trim() || undefined);
    else if (chosen.key === "update_tracking") r = await fulfilAndTrack(gid, trackingNo.trim(), carrierName.trim() || undefined);
    else if (chosen.key === "remove_tracking") r = await clearTracking(gid);
    else if (chosen.key === "cancel") r = await cancelOrder(gid);
    else if (chosen.key === "refund") r = await markRefunded(gid);
    else r = await markPartiallyRefunded(gid, parseFloat(amount));
    setBusy(false); setResult(r);
    if (r.ok) { setAction(""); onDone(); }
  };

  if (!gid) {
    return (
      <div className="small text-inverse text-opacity-50 mt-2">
        This order is not in the commerce engine, so it cannot be actioned here.
      </div>
    );
  }

  return (
    <div className="mt-3">
      <select className="form-select form-select-sm mb-2" value={action} onChange={(e) => { setAction(e.target.value); setResult(null); }}>
        <option value="">Choose an action…</option>
        {ACTIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
      </select>

      {chosen?.needsTracking ? (
        <>
          <Field label="Tracking number" value={trackingNo} onChange={setTrackingNo} />
          <Field label="Carrier" value={carrierName} onChange={setCarrierName} placeholder="UPS / USPS / FedEx / DHL" />
          <div className="small text-inverse text-opacity-50 mb-2">
            The carrier is sent to PayPal — without it the buyer gets a tracking link that resolves to nothing.
          </div>
        </>
      ) : null}

      {chosen?.needsAmount ? <Field label="Refund amount" value={amount} onChange={setAmount} placeholder="0.00" /> : null}

      {chosen ? (
        <button className="btn btn-sm btn-theme" disabled={busy} onClick={apply}>
          {busy ? "Working…" : chosen.label}
        </button>
      ) : null}
      <Feedback result={result} />
      {status ? <div className="small text-inverse text-opacity-50 mt-2">Current status: {status}</div> : null}
    </div>
  );
}
